import { useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { queryClient } from "@/lib/queryClient";
import { ThemeToggle } from "@/components/ThemeToggle";
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from "@/components/ui/tooltip";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { StatsCard } from "@/components/StatsCard";
import { TournamentList } from "@/components/TournamentList";
import { TournamentForm } from "@/components/TournamentForm";
import { TournamentDetail } from "@/components/TournamentDetail";
import {
  Building2,
  Trophy,
  Calendar,
  Users,
  Target,
  Settings,
  LayoutDashboard,
  RefreshCw,
  LogOut,
} from "lucide-react";
import type { ClubProfile as ClubProfileType } from "@shared/schema";

export function ClubDashboard() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState("tournaments");
  const [tournamentView, setTournamentView] = useState<
    "list" | "create" | "edit" | "detail"
  >("list");
  const [selectedTournamentId, setSelectedTournamentId] = useState<string | null>(null);
  const { toast: _toast } = useToast();

  const clubProfile = user?.profile as ClubProfileType | null;

  const handleLogout = () => {
    import("@/lib/sessionPersistence").then(({ logout }) => logout());
  };

  const handleViewTournament = (id: string) => {
    setSelectedTournamentId(id);
    setTournamentView("detail");
  };

  const handleEditTournament = (id: string) => {
    setSelectedTournamentId(id);
    setTournamentView("edit");
  };

  const handleBackToList = () => {
    setSelectedTournamentId(null);
    setTournamentView("list");
  };

  return (
    <div className="min-h-screen bg-background pb-14 lg:pb-0">
      <header
        className="sticky top-0 z-40 flex items-center justify-between px-4 py-3 bg-background border-b border-border"
        style={{ paddingTop: "max(0.75rem, env(safe-area-inset-top, 0px))" }}
      >
        <div className="flex items-center gap-2">
          <Building2 className="h-6 w-6 text-primary" />
          <span className="font-bold text-lg tracking-tight">CourtVibe</span>
          <span className="text-sm text-muted-foreground hidden sm:block">| Panel de Club</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground hidden sm:block">
            {clubProfile?.clubName || user?.email}
          </span>
          <ThemeToggle />
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => queryClient.refetchQueries()}
                data-testid="button-refresh"
                aria-label="Actualizar datos"
              >
                <RefreshCw className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Actualizar datos</TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                onClick={handleLogout}
                data-testid="button-logout"
                aria-label="Cerrar sesión"
              >
                <LogOut className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Cerrar sesión</TooltipContent>
          </Tooltip>
        </div>
      </header>

      <div className="flex min-h-[calc(100vh-57px)]">
        <aside className="hidden lg:flex flex-col w-64 border-r border-border bg-card p-4">
          <nav className="space-y-1">
            <Button
              variant={activeTab === "dashboard" ? "secondary" : "ghost"}
              className="w-full justify-start"
              onClick={() => setActiveTab("dashboard")}
              data-testid="nav-dashboard"
            >
              <LayoutDashboard className="h-4 w-4 mr-2" />
              Inicio
            </Button>
            <Button
              variant={activeTab === "tournaments" ? "secondary" : "ghost"}
              className="w-full justify-start"
              onClick={() => { setActiveTab("tournaments"); setTournamentView("list"); }}
              data-testid="nav-tournaments"
            >
              <Trophy className="h-4 w-4 mr-2" />
              Torneos
            </Button>
            <Button
              variant={activeTab === "courts" ? "secondary" : "ghost"}
              className="w-full justify-start"
              onClick={() => setActiveTab("courts")}
              data-testid="nav-courts"
            >
              <Calendar className="h-4 w-4 mr-2" />
              Pistas
            </Button>
            <Button
              variant={activeTab === "members" ? "secondary" : "ghost"}
              className="w-full justify-start"
              onClick={() => setActiveTab("members")}
              data-testid="nav-members"
            >
              <Users className="h-4 w-4 mr-2" />
              Miembros
            </Button>
            <Button
              variant={activeTab === "settings" ? "secondary" : "ghost"}
              className="w-full justify-start"
              onClick={() => setActiveTab("settings")}
              data-testid="nav-settings"
            >
              <Settings className="h-4 w-4 mr-2" />
              Configuración
            </Button>
          </nav>
        </aside>

        <main className="flex-1 p-4 lg:p-6 max-w-6xl">
          <div className="lg:hidden mb-4">
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="grid w-full grid-cols-4">
                <TabsTrigger value="dashboard"><LayoutDashboard className="h-4 w-4" /></TabsTrigger>
                <TabsTrigger value="tournaments"><Trophy className="h-4 w-4" /></TabsTrigger>
                <TabsTrigger value="courts"><Calendar className="h-4 w-4" /></TabsTrigger>
                <TabsTrigger value="members"><Users className="h-4 w-4" /></TabsTrigger>
              </TabsList>
            </Tabs>
          </div>

          {activeTab === "dashboard" && (
            <div className="space-y-6">
              <div>
                <h2 className="text-2xl font-bold mb-2">
                  ¡Bienvenido, {clubProfile?.clubName || "Club"}!
                </h2>
                <p className="text-muted-foreground">Resumen de la actividad de tu club</p>
              </div>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <StatsCard title="Torneos Activos" value="0" icon={<Trophy className="h-5 w-5" />} />
                <StatsCard title="Pistas" value={String(clubProfile?.numberOfCourts || 0)} icon={<Calendar className="h-5 w-5" />} />
                <StatsCard title="Miembros" value={String(clubProfile?.totalMembers || 0)} icon={<Users className="h-5 w-5" />} />
                <StatsCard
                  title="Valoración"
                  value={clubProfile?.averageRating ? `${clubProfile.averageRating.toFixed(1)}/5` : "-"}
                  icon={<Target className="h-5 w-5" />}
                />
              </div>
              <div className="grid lg:grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Trophy className="h-5 w-5 text-primary" />
                      Próximos Torneos
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-center py-8 text-muted-foreground">
                      <Trophy className="h-12 w-12 mx-auto mb-2 opacity-30" />
                      <p>No hay torneos programados</p>
                      <Button
                        variant="ghost"
                        onClick={() => { setActiveTab("tournaments"); setTournamentView("create"); }}
                        className="mt-2"
                      >
                        Crear primer torneo
                      </Button>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Calendar className="h-5 w-5 text-primary" />
                      Reservas de Hoy
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-center py-8 text-muted-foreground">
                      <Calendar className="h-12 w-12 mx-auto mb-2 opacity-30" />
                      <p>No hay reservas para hoy</p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          )}

          {activeTab === "tournaments" && (
            <>
              {tournamentView === "list" && (
                <TournamentList
                  onCreateTournament={() => setTournamentView("create")}
                  onViewTournament={handleViewTournament}
                  onEditTournament={handleEditTournament}
                />
              )}
              {tournamentView === "create" && (
                <TournamentForm onBack={handleBackToList} onSuccess={handleBackToList} />
              )}
              {tournamentView === "edit" && selectedTournamentId && (
                <TournamentForm
                  tournamentId={selectedTournamentId}
                  onBack={() => setTournamentView("detail")}
                  onSuccess={() => setTournamentView("detail")}
                />
              )}
              {tournamentView === "detail" && selectedTournamentId && (
                <TournamentDetail
                  tournamentId={selectedTournamentId}
                  onBack={handleBackToList}
                  onEdit={() => setTournamentView("edit")}
                />
              )}
            </>
          )}

          {activeTab === "courts" && (
            <div className="space-y-6">
              <div>
                <h2 className="text-2xl font-bold mb-2">Gestión de Pistas</h2>
                <p className="text-muted-foreground">Administra las reservas y disponibilidad de tus pistas</p>
              </div>
              <Card>
                <CardContent className="py-12 text-center">
                  <Calendar className="h-16 w-16 mx-auto mb-4 text-muted-foreground/30" />
                  <h3 className="text-lg font-semibold mb-2">Próximamente</h3>
                  <p className="text-muted-foreground">La gestión de pistas y reservas estará disponible pronto</p>
                </CardContent>
              </Card>
            </div>
          )}

          {activeTab === "members" && (
            <div className="space-y-6">
              <div>
                <h2 className="text-2xl font-bold mb-2">Miembros del Club</h2>
                <p className="text-muted-foreground">Gestiona los jugadores asociados a tu club</p>
              </div>
              <Card>
                <CardContent className="py-12 text-center">
                  <Users className="h-16 w-16 mx-auto mb-4 text-muted-foreground/30" />
                  <h3 className="text-lg font-semibold mb-2">Próximamente</h3>
                  <p className="text-muted-foreground">La gestión de miembros estará disponible pronto</p>
                </CardContent>
              </Card>
            </div>
          )}

          {activeTab === "settings" && (
            <div className="space-y-6">
              <div>
                <h2 className="text-2xl font-bold mb-2">Configuración del Club</h2>
                <p className="text-muted-foreground">Actualiza la información de tu club</p>
              </div>
              <Card>
                <CardHeader>
                  <CardTitle>Información del Club</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-muted-foreground">Nombre</p>
                      <p className="font-medium">{clubProfile?.clubName || "-"}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Ciudad</p>
                      <p className="font-medium">{clubProfile?.city || "-"}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Teléfono</p>
                      <p className="font-medium">{clubProfile?.phone || "-"}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Email</p>
                      <p className="font-medium">{clubProfile?.email || "-"}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Número de Pistas</p>
                      <p className="font-medium">{clubProfile?.numberOfCourts || "-"}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Horario</p>
                      <p className="font-medium">
                        {clubProfile?.openingTime && clubProfile?.closingTime
                          ? `${clubProfile.openingTime} - ${clubProfile.closingTime}`
                          : "-"}
                      </p>
                    </div>
                  </div>
                  {clubProfile?.description && (
                    <div>
                      <p className="text-sm text-muted-foreground">Descripción</p>
                      <p className="font-medium">{clubProfile.description}</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
